import { useState } from "react";
import { X, Mail, CheckCircle, AlertCircle } from "lucide-react";
import { getApiBaseUrl } from "../apis/apiBase";

const API_BASE_URL = getApiBaseUrl();

export function ForgotPasswordModal({ isOpen, onClose, onSubmit }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError("Please enter your email address");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE_URL}/password-reset/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ emailid: email }),
      });
    
      const data = await response.json();
   
      if (response.ok) {
        setSuccess(true);
        // Call the parent onSubmit if needed
        if (onSubmit) {
          onSubmit(email);
        }
        // Auto close after 3 seconds
        setTimeout(() => {
          handleClose();
        }, 3000);
      } else {
        // Extract message from nested response structure
        const errorMessage = data.body?.message || data.message || "An error occurred";
        
        // Handle different error status codes
        switch (response.status) {
          case 404:
            setError("Email not found. Please check your email address or create an account.");
            break;
          case 400:
            setError(errorMessage.includes("required") ? "Please enter an email address." : errorMessage);
            break;
          case 429:
            setError("Too many attempts. Please try again later.");
            break;
          case 500:
            setError("Server error. Please try again later.");
            break;
          default:
            setError(errorMessage);
        }
      }
    } catch (error) {
      console.error("Network error:", error);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail("");
    setError("");
    setSuccess(false);
    setLoading(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-black/90 border border-white/20 rounded-lg p-6 w-full max-w-md backdrop-blur-sm">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">
            {success ? "Check Your Email" : "Forgot Password"}
          </h2>
          <button
            onClick={handleClose}
            className="text-white/70 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {success ? (
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <CheckCircle className="w-16 h-16 text-green-400" />
            </div>
            <h3 className="text-lg font-medium text-white mb-2">Email Sent!</h3>
            <p className="text-white/70 text-sm mb-4">
              If an account with that email exists, we've sent a password reset link to:
            </p>
            <p className="text-white font-medium mb-4">{email}</p>
            <p className="text-white/50 text-xs">
              Please check your inbox and spam folder. The link will expire in 1 hour.
            </p>
            <p className="text-white/40 text-xs mt-4">
              This window will close automatically in a few seconds...
            </p>
          </div>
        ) : (
          <div>
            <p className="text-white/70 text-sm mb-4">
              Enter your email address and we'll send you a link to reset your password.
            </p>

            {error && (
              <div className="bg-red-900/30 border border-red-500/50 text-red-200 px-3 py-2 rounded-md mb-4 flex items-center">
                <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div className="space-y-4">
              <div className="relative">
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError(""); // Clear error when user types
                  }}
                  className="w-full bg-transparent border-b border-white/30 rounded-none px-8 py-3 text-white placeholder:text-white/70 focus:outline-none focus:border-white transition-colors"
                  disabled={loading}
                />
                <Mail className="absolute left-0 top-3 h-5 w-5 text-white/50" />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading}
                  className="flex-1 bg-transparent border border-white/30 text-white font-medium py-3 rounded-full hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading || !email}
                  className="flex-1 bg-white hover:bg-white/90 text-black font-medium py-3 rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    "Send Reset Link"
                  )}
                </button>
              </div>
            </div>

            <div className="mt-4 text-center">
              <p className="text-white/50 text-xs">
                Remember your password?{' '}
                <button
                  onClick={handleClose}
                  className="text-white hover:text-white/80 underline transition-colors"
                >
                  Back to Login
                </button>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}